/**
 * Ladder GROUPS of legacy one-contract lines, for the Signals harvest card (CIO, 2026-09-17).
 *
 * A one-contract line can never stage a 2x trim, so neighbouring one-lots in one expiry are
 * harvested as ONE position: the group triggers on its COMBINED multiple, sells the deepest in
 * the money member(s), and keeps the rest as runner lines — once per group. The rule lives in
 * HelionBackend/strategy/harvest_cohorts.py and is enforced there; this module only reads what
 * `home_run_harvest` publishes under `cohorts` and puts it into words.
 *
 * NOTHING IS DERIVED HERE. The combined multiple, which member would be sold, how many runners
 * remain and whether the group is ready all come from the server, so the card cannot disagree
 * with the engine that stages the ticket. Missing fields read as unknown — never as zero and
 * never as "nothing to harvest".
 */

/** Statuses (strategy/harvest_cohorts.py STATUS_*). */
export type CohortStatus =
  | "harvested"
  /** A configured member is no longer held and no harvest was recorded: the group is frozen. */
  | "incomplete"
  | "inactive"
  | "unpriced"
  | "below_trigger"
  | "ready";

export type CohortMember = {
  conid: string;
  strike: number | null;
  quantity: number | null;
  multiple: number | null;
  inGroup: boolean;
  excludedReason: string | null;
};

export type CohortRow = {
  key: string;
  label: string;
  expiration: string;
  status: CohortStatus | string;
  members: CohortMember[];
  heldMemberCount: number;
  combinedMultiple: number | null;
  triggerMultiple: number | null;
  sellQty: number;
  sellStrikes: number[];
  runnerCount: number | null;
  alreadyHarvested: boolean;
  harvestedOn: string | null;
  harvestMemoryUnavailable: boolean;
  /** The server's own "this is eligible to stage" flag. Ready is not staged. */
  canStage: boolean;
  missingMembers: string[];
  stagingEnabled: boolean;
  configProblems: string[];
};

const num = (v: unknown): number | null => {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

/** Read the `cohorts` block. Returns [] when absent — the card then shows nothing, which is
 *  correct for a book with no grouped lines. */
export function readCohorts(homeRun: unknown): CohortRow[] {
  const raw = (homeRun as { cohorts?: unknown } | null | undefined)?.cohorts;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((g): g is Record<string, unknown> => !!g && typeof g === "object")
    .map((g) => {
      const members = Array.isArray(g.members) ? (g.members as Record<string, unknown>[]) : [];
      const sell = Array.isArray(g.sell) ? (g.sell as Record<string, unknown>[]) : [];
      return {
        key: String(g.key ?? ""),
        label: String(g.label ?? g.key ?? ""),
        expiration: String(g.expiration ?? ""),
        status: String(g.status ?? ""),
        members: members.map((m) => ({
          conid: String(m.conid ?? ""),
          strike: num(m.strike),
          quantity: num(m.quantity),
          multiple: num(m.multiple),
          inGroup: m.inGroup === true,
          excludedReason: typeof m.excludedReason === "string" ? m.excludedReason : null,
        })),
        heldMemberCount: num(g.heldMemberCount) ?? 0,
        combinedMultiple: num(g.combinedMultiple),
        triggerMultiple: num(g.triggerMultiple),
        sellQty: num(g.sellQty) ?? 0,
        sellStrikes: sell.map((s) => num(s.strike)).filter((s): s is number => s !== null),
        runnerCount: num(g.runnerCount),
        alreadyHarvested: g.alreadyHarvested === true,
        harvestedOn: typeof g.harvestedOn === "string" ? g.harvestedOn : null,
        harvestMemoryUnavailable: g.harvestMemoryUnavailable === true,
        canStage: g.canStage === true,
        missingMembers: Array.isArray(g.missingMembers) ? (g.missingMembers as unknown[]).map(String) : [],
        stagingEnabled: g.stagingEnabled === true,
        configProblems: Array.isArray(g.configProblems) ? (g.configProblems as unknown[]).map(String) : [],
      };
    })
    .filter((g) => g.key);
}

/** Tones are the cockpit chip vocabulary (components/cockpit ChipTone), not a second set. */
export type CohortVerdict = { tone: "positive" | "info" | "warning" | "neutral"; headline: string; detail: string };

/**
 * What the group is doing, in the CIO's terms. The headline is the state; the detail is what
 * happens next or why nothing does.
 */
export function describeCohort(row: CohortRow): CohortVerdict {
  const trigger = row.triggerMultiple !== null ? `${row.triggerMultiple.toFixed(2)}x` : "the trigger";
  const combined = row.combinedMultiple !== null ? `${row.combinedMultiple.toFixed(2)}x` : "—";
  const lines = (n: number) => `${n} line${n === 1 ? "" : "s"}`;

  if (row.configProblems.length) {
    return { tone: "warning", headline: "Grouping off", detail: `The group list is malformed, so nothing is grouped: ${row.configProblems[0]}` };
  }
  if (row.alreadyHarvested) {
    return {
      tone: "neutral",
      headline: "Harvested",
      detail: `Harvested${row.harvestedOn ? ` on ${row.harvestedOn}` : ""}. Each group is harvested once, so what is left are its runners.`,
    };
  }
  if (row.status === "incomplete") {
    return {
      tone: "warning",
      headline: "Frozen",
      detail: `${row.missingMembers.length || "A"} line${row.missingMembers.length === 1 ? "" : "s"} of this group ${
        row.missingMembers.length === 1 ? "is" : "are"
      } no longer held, with no harvest recorded. The group is frozen until that is explained, so nothing further is sold from it.`,
    };
  }
  if (row.harvestMemoryUnavailable) {
    return {
      tone: "warning",
      headline: "Cannot confirm",
      detail: "The harvest record could not be read, so it cannot be confirmed whether this group was already harvested. No sale is staged until it can.",
    };
  }
  if (row.status === "inactive") {
    return {
      tone: "neutral",
      headline: "Not a group",
      detail: `Only ${lines(row.heldMemberCount)} left. Selling it would be a full exit with no runner, which is not a harvest.`,
    };
  }
  if (row.status === "unpriced") {
    return { tone: "warning", headline: "Unpriced", detail: "A member has no usable mark, so the combined multiple cannot be struck." };
  }
  if (row.status === "ready") {
    const strikes = row.sellStrikes.length ? row.sellStrikes.map((s) => s.toLocaleString()).join(" and ") : "the deepest in the money";
    // READY IS NOT STAGED (reviewed 2026-09-17). The exit scan still has to run, and its own
    // gates -- an unknown regime, the staging guard, a ticket already open -- can stop it. The
    // card says what will happen, never that a ticket exists.
    const staged = !row.stagingEnabled
      ? "Staging is switched off, so no ticket will be created."
      : row.canStage
      ? "The next exit scan stages a sell ticket for your approval."
      : "The engine has not cleared it to stage yet.";
    return {
      tone: "positive",
      headline: `Ready at ${combined}`,
      detail: `Sell ${strikes} (${row.sellQty} of ${row.heldMemberCount}), keeping ${lines(row.runnerCount ?? 0)} as runners. ${staged}`,
    };
  }
  return {
    tone: "info",
    headline: `${combined} combined`,
    detail: `Below the ${trigger} group trigger. ${lines(row.heldMemberCount)} held; the deepest in the money would be sold first.`,
  };
}
